import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTracking } from '@justgiving/react-tracking';
import gql from 'graphql-tag';

import {
  PageStatus,
  CampaignStartFundraisingFragment,
} from '../../../types/__generated__';
import classNames from 'classnames';
import styles from './CampaignStartFundraising.scss';
import { filterByTypename } from '../../utils/graphql';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';

const FRAGMENT = buildQuery(gql`
  fragment CampaignStartFundraising on Page {
    startFundraisingUrl
    status
    relationships {
      config {
        membersDisabled
      }
      beneficiaries(first: 1) {
        totalCount
        nodes {
          ... on Charity {
            name
            profilePageUrl
          }
        }
      }
    }
  }
`);

const CampaignStartFundraising: FunctionComponent = () => {
  const result = usePageQuery<CampaignStartFundraisingFragment>(FRAGMENT);
  const track = useTracking();
  const { t } = useTranslation('common', { useSuspense: false });

  if (!UsePageQueryResult.hasData(result) || !result.data.startFundraisingUrl) {
    return null;
  }

  const { status, startFundraisingUrl, relationships } = result.data;

  const beneficiaries = relationships?.beneficiaries;
  const membersDisabled = relationships?.config?.membersDisabled;
  if (!beneficiaries || membersDisabled) {
    return null;
  }
  const charities = filterByTypename(beneficiaries.nodes, 'Charity');

  return (
    <section className={classNames(styles.container)}>
      <h2 className="jg-h4">{t('campaign.startFundraisingTitle')}</h2>
      {status === PageStatus.Completed && (
        <>
          <p>{t('campaign.startFundraisingCompleted')}</p>
          <a
            className={classNames('jg-btn', 'jg-btn--ghost')}
            href={`${charities[0].profilePageUrl}`}
            onClick={() => {
              track({
                event: 'link click',
                subtype: 'button',
                event_value: 'view charity profile',
                page_section: 'become a fundraiser',
              });
            }}
          >
            {t('campaign.startFundraisingCompletedCTA')}
          </a>
        </>
      )}

      {status !== PageStatus.Completed && (
        <>
          <p>{t('campaign.startFundraising')}</p>
          <a
            className={classNames('jg-btn', 'jg-btn--primary')}
            href={startFundraisingUrl}
            onClick={() => {
              track({
                event: 'link click',
                subtype: 'button',
                event_value: 'start fundraising',
                page_section: 'become a fundraiser',
              });
            }}
          >
            {t('campaign.startFundraisingCTA')}
          </a>
        </>
      )}
    </section>
  );
};

export default CampaignStartFundraising;
